import { useState } from 'react';
import styled from 'styled-components';
import { createAspectRatio, media } from '@higo/ui';
import { VisualExaminationDiagnosticVideoViewerWithToggling } from './VisualExaminationDiagnosticVideoViewer';
import { VisualExaminationDiagnosticViewerProps } from './VisualExaminationDiagnosticViewerProps';
import { VisualExaminationDiagnosticGalleryViewer } from './VisualExaminationDiagnosticGalleryViewer';
import { BaseButton } from '@higo/ui';
import { ExaminationStatusLabel } from '../../../../components/ExaminationStatusLabel';
import { DataErrorInfo } from '../../../../components/LoadingAndErrorHandlingItems';

const DiagnosticVideoContainer = styled.div`
  ${createAspectRatio(4, 3)}
  width: calc(100% - 4rem);
  display: flex;
  margin: 0 auto;
  ${media.mobile.max} {
    width: 100%;
  }
  ${media.mobile.min}, ${media.tablet.min} { {
    width: 640px;
    margin: 0 auto;
  }


`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: calc(100% - 8rem);
  ${media.mobile.min}, ${media.tablet.min} {
    width: 640px;
  }
  ${media.mobile.max} {
    width: 100%;
  }

  #gallery-button {
    margin-right: 1px;
  }
  #video-button {
    margin-left: 1px;
  }
`;

const StyledButton = styled(BaseButton)`
  height: 2.25rem;
  width: calc(100% - 2rem);
  align-items: center;
  justify-content: center;
`;

const MessageContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
  text-align: center;
`;

const CanceledInfoLocal = styled.p`
  margin-top: 0.8rem;
  margin-bottom: 2rem;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.palette.secondary[500]};
`;

export type WithTogglingViewerButtonProps = {
  buttons: object;
  setButtons: any;
  handleButtonsChange: any;
};

export const WithTogglingViewer = (
  props: VisualExaminationDiagnosticViewerProps,
) => {
  const { data, isLoading, isError } =
    props.model.usePhysicalExaminationResultsQuery(props.examinationId);

  const {
    data: galleryResults,
    isFetched: isGalleryFetched,
    isError: isGalleryError,
  } = props.model.usePhysicalExaminationGalleryResultsLinks(
    props.examinationId,
  );

  const [buttons, setButtons] = useState([
    { label: 'Diagnostic Frames', value: true },
    { label: 'Full video', value: false },
  ]);

  const isNoData =
    !isGalleryFetched ||
    Boolean(isGalleryError) ||
    !galleryResults ||
    (Array.isArray(galleryResults) && galleryResults.length === 0) ||
    !props.rowData ||
    (Array.isArray(props.rowData) && props.rowData.length === 0);

  const shouldHide = props.isStatusCanceledByUser || isNoData;

  if (shouldHide) {
    if (props.isStatusCanceledByUser) {
      return (
        <MessageContainer>
          <CanceledInfoLocal>
            <ExaminationStatusLabel status="CANCELED" />
          </CanceledInfoLocal>
        </MessageContainer>
      );
    }

    return (
      <MessageContainer>
        <DataErrorInfo />
      </MessageContainer>
    );
  }
  const diagnosticItemIndex = data?.diagnosticImageIndex;
  const diagnosticItemName = props.rowData
    ? props.rowData[diagnosticItemIndex]?.filename
    : undefined;

  

  const handleButtonsChange =
    (props: WithTogglingViewerButtonProps) => (label: string) => {
      const newButtonsState = buttons.map((button) => {
        if (button.label === label) {
          return (button = { label: button.label, value: true });
        }
        return {
          label: button.label,
          value: false,
        };
      });
      setButtons(newButtonsState);
    };

  const VisualExaminationDiagnosticTabButton = (
    label: string,
    value: boolean,
    isVideoButton: boolean,
  ) => {
    return (
      <StyledButton
        style={{
          background: value ? '#39C0FE' : '#FFFFFF',
          border: value ? 'none' : '1px solid #8E94B0',
          color: value ? '#FFFFFF' : '#8E94B0',
        }}
        id={isVideoButton ? 'video-button' : 'gallery-button'}
        onClick={() =>
          handleButtonsChange({
            buttons,
            setButtons,
            handleButtonsChange: undefined,
          })(label)
        }
      >
        {isVideoButton && !data?.video
          ? props.requestGenerateVideoButtonSlot
          : label}
      </StyledButton>
    );
  };

  return (
    <>
      <ButtonsContainer>
        {VisualExaminationDiagnosticTabButton(
          buttons[0].label,
          buttons[0].value,
          false,
        )}
        {VisualExaminationDiagnosticTabButton(
          buttons[1].label,
          buttons[1].value,
          true,
        )}
      </ButtonsContainer>

      <>
        {buttons[0].value && (
          <VisualExaminationDiagnosticGalleryViewer
            examinationId={props.examinationId}
            diagnosticImageIndex={data?.diagnosticImageIndex}
            diagnosticItemName={diagnosticItemName}
            model={props.model}
            galleryViewerProps={props.galleryViewerProps}
            id="visual-gallery-viewer"
            setActiveGalleryLink={props.setActiveGalleryLink}
          />
        )}

        {isLoading && <div>loading video</div>}
        {isError && <div>error video</div>}

        {buttons[1].value && (
          <DiagnosticVideoContainer id="diagnostic-video-container">
            <VisualExaminationDiagnosticVideoViewerWithToggling
              examinationId={props.examinationId}
              model={props.model}
            />
          </DiagnosticVideoContainer>
        )}
      </>
    </>
  );
};
